/**
 * Petal-burst particles.
 *
 * One pooled `Points` cloud serves the whole scene. Bursts write into free
 * slots and dead particles are recycled, so a long session never allocates
 * and never triggers a GC pause mid-tap.
 */

import * as THREE from 'three';

const MAX_PARTICLES = 320;
const GRAVITY = -0.55;

/** Soft radial sprite, drawn once to a canvas. Avoids shipping a texture. */
function makeSprite(): THREE.Texture {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.45, 'rgba(255,255,255,0.75)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(canvas);
  tex.needsUpdate = true;
  return tex;
}

export class ParticleSystem {
  readonly points: THREE.Points;

  private positions = new Float32Array(MAX_PARTICLES * 3);
  private colors = new Float32Array(MAX_PARTICLES * 3);
  private sizes = new Float32Array(MAX_PARTICLES);
  private velocities = new Float32Array(MAX_PARTICLES * 3);
  private life = new Float32Array(MAX_PARTICLES);
  private maxLife = new Float32Array(MAX_PARTICLES);
  /* Per-particle size multiplier. `update` recomputes size from remaining
     life, so without this every weather mote inherited the petal-burst curve
     and rain fell as five-centimetre snowballs. */
  private sizeScale = new Float32Array(MAX_PARTICLES);
  private cursor = 0;

  private geometry: THREE.BufferGeometry;
  private material: THREE.PointsMaterial;
  private sprite: THREE.Texture;

  constructor() {
    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));
    this.geometry.setAttribute('color', new THREE.BufferAttribute(this.colors, 3));
    this.geometry.setAttribute('size', new THREE.BufferAttribute(this.sizes, 1));

    this.sprite = makeSprite();
    this.material = new THREE.PointsMaterial({
      size: 0.035,
      map: this.sprite,
      vertexColors: true,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true,
    });

    this.points = new THREE.Points(this.geometry, this.material);
    this.points.frustumCulled = false;

    // Stow every particle far away and dead.
    for (let i = 0; i < MAX_PARTICLES; i++) {
      this.positions[i * 3 + 1] = -1000;
    }
  }

  /**
   * Fire `count` particles outward from `origin`.
   * `spread` scales the initial velocity cone.
   */
  burst(origin: THREE.Vector3, color: number, count = 26, spread = 1): void {
    const c = new THREE.Color(color);
    for (let n = 0; n < count; n++) {
      const i = this.cursor;
      this.cursor = (this.cursor + 1) % MAX_PARTICLES;

      const i3 = i * 3;
      this.positions[i3] = origin.x;
      this.positions[i3 + 1] = origin.y;
      this.positions[i3 + 2] = origin.z;

      // Upward-biased hemisphere — petals should lift before they fall.
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI * 0.42;
      const speed = (0.5 + Math.random() * 0.75) * spread;
      this.velocities[i3] = Math.sin(phi) * Math.cos(theta) * speed;
      this.velocities[i3 + 1] = (0.55 + Math.cos(phi) * 0.85) * speed;
      this.velocities[i3 + 2] = Math.sin(phi) * Math.sin(theta) * speed;

      // Vary brightness per particle so the burst has depth.
      const shade = 0.65 + Math.random() * 0.35;
      this.colors[i3] = c.r * shade;
      this.colors[i3 + 1] = c.g * shade;
      this.colors[i3 + 2] = c.b * shade;

      this.sizeScale[i] = 1;
      this.sizes[i] = 0.6 + Math.random() * 0.8;
      this.maxLife[i] = 0.9 + Math.random() * 0.7;
      this.life[i] = this.maxLife[i];
    }
  }

  /**
   * Weather motes: rain falling into the plot, or dust blowing across it.
   *
   * Shares the same pool as the bursts rather than getting its own system.
   * That is deliberate — the pool is a fixed 320 and a burst simply recycles
   * the oldest motes, so a downpour can never starve the bloom that a player
   * is actually looking at. It also means weather costs no extra draw call.
   */
  fall(
    center: THREE.Vector3,
    radius: number,
    height: number,
    color: number,
    count: number,
    drift: number,
    speed: number,
  ): void {
    const c = new THREE.Color(color);
    for (let n = 0; n < count; n++) {
      const i = this.cursor;
      this.cursor = (this.cursor + 1) % MAX_PARTICLES;
      const i3 = i * 3;

      // Square-root radius keeps the scatter even rather than clustered at
      // the middle, which is what a uniform random radius would give.
      const a = Math.random() * Math.PI * 2;
      const r = Math.sqrt(Math.random()) * radius;
      this.positions[i3] = center.x + Math.cos(a) * r;
      this.positions[i3 + 1] = center.y + height * (0.55 + Math.random() * 0.45);
      this.positions[i3 + 2] = center.z + Math.sin(a) * r;

      this.velocities[i3] = (Math.random() - 0.5) * drift;
      this.velocities[i3 + 1] = -speed * (0.7 + Math.random() * 0.6);
      this.velocities[i3 + 2] = (Math.random() - 0.5) * drift;

      const shade = 0.6 + Math.random() * 0.4;
      this.colors[i3] = c.r * shade;
      this.colors[i3 + 1] = c.g * shade;
      this.colors[i3 + 2] = c.b * shade;

      this.sizeScale[i] = 0.34;
      this.sizes[i] = 0.35 + Math.random() * 0.4;
      this.maxLife[i] = 0.8 + Math.random() * 0.6;
      this.life[i] = this.maxLife[i];
    }
  }

  update(dt: number): void {
    let anyAlive = false;
    for (let i = 0; i < MAX_PARTICLES; i++) {
      if (this.life[i] <= 0) continue;
      anyAlive = true;

      this.life[i] -= dt;
      const i3 = i * 3;

      if (this.life[i] <= 0) {
        this.positions[i3 + 1] = -1000;
        continue;
      }

      this.velocities[i3 + 1] += GRAVITY * dt;
      // Light drag so particles settle rather than streaking away.
      const drag = 1 - Math.min(1, dt * 1.1);
      this.velocities[i3] *= drag;
      this.velocities[i3 + 2] *= drag;

      this.positions[i3] += this.velocities[i3] * dt;
      this.positions[i3 + 1] += this.velocities[i3 + 1] * dt;
      this.positions[i3 + 2] += this.velocities[i3 + 2] * dt;

      // Fade by shrinking; additive blending handles the opacity read.
      const t = this.life[i] / this.maxLife[i];
      this.colors[i3] *= 0.995;
      this.colors[i3 + 1] *= 0.995;
      this.colors[i3 + 2] *= 0.995;
      this.sizes[i] = t * 1.4 * this.sizeScale[i];
    }

    if (anyAlive) {
      this.geometry.attributes.position.needsUpdate = true;
      this.geometry.attributes.color.needsUpdate = true;
      this.geometry.attributes.size.needsUpdate = true;
    }
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
    this.sprite.dispose();
  }
}
